import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { UserCheck, Bell, X, Layers, Server } from "lucide-react";
import {
  environmentsApi,
  environmentApproversApi,
  environmentDeployersApi,
  groupsApi,
  groupApproversApi,
  groupNotifiersApi,
  approvalRolesApi,
  usersApi,
  EnvironmentApprover,
  EnvironmentDeployer,
  GroupApprover,
  GroupNotifier,
  User,
  EligibleApprover,
} from "@/api/client";

type ConfigLevel = "group" | "environment";

export default function DefaultApproversSettings() {
  const [configLevel, setConfigLevel] = useState<ConfigLevel>("group");
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [selectedEnvironmentId, setSelectedEnvironmentId] = useState<
    string | null
  >(null);

  const { data: groupsData, isLoading: groupsLoading } = useQuery({
    queryKey: ["groups"],
    queryFn: () => groupsApi.list(),
  });

  const { data: environmentsData, isLoading: environmentsLoading } = useQuery({
    queryKey: ["environments"],
    queryFn: () => environmentsApi.list(),
  });

  const selectedGroup = groupsData?.groups?.find(
    (g) => g.id === selectedGroupId,
  );

  const selectedEnvironment = environmentsData?.environments?.find(
    (e) => e.id === selectedEnvironmentId,
  );

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">
          Default Approvers & Notifiers
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          Configure default approvers and deployment notifiers at the group or
          environment level. Group defaults apply to all environments in the
          group unless overridden.
        </p>
      </div>

      {/* Level Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => {
              setConfigLevel("group");
              setSelectedEnvironmentId(null);
            }}
            className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm ${
              configLevel === "group"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            <Layers className="h-4 w-4" />
            Group Defaults
          </button>
          <button
            onClick={() => {
              setConfigLevel("environment");
              setSelectedGroupId(null);
            }}
            className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm ${
              configLevel === "environment"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            <Server className="h-4 w-4" />
            Environment Overrides
          </button>
        </nav>
      </div>

      {/* Group Level Configuration */}
      {configLevel === "group" && (
        <>
          <div className="mb-6">
            <label htmlFor="group" className="label">
              Select Group
            </label>
            <select
              id="group"
              value={selectedGroupId || ""}
              onChange={(e) => setSelectedGroupId(e.target.value || null)}
              className="input max-w-md"
              disabled={groupsLoading}
            >
              <option value="">Choose a group...</option>
              {groupsData?.groups?.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </select>
          </div>

          {selectedGroupId && selectedGroup ? (
            <div className="card">
              <div className="mb-4">
                <h2 className="text-lg font-medium text-gray-900">
                  {selectedGroup.name}
                </h2>
                {selectedGroup.description && (
                  <p className="text-sm text-gray-500 mt-1">
                    {selectedGroup.description}
                  </p>
                )}
                <p className="text-sm text-blue-600 mt-2">
                  These defaults apply to all environments in this group unless
                  overridden at the environment level.
                </p>
              </div>

              <GroupApproversNotifiers groupId={selectedGroupId} />
            </div>
          ) : (
            <div className="card text-center py-12">
              <p className="text-gray-500">
                Select a group above to manage its default approvers and
                notifiers.
              </p>
            </div>
          )}
        </>
      )}

      {/* Environment Level Configuration */}
      {configLevel === "environment" && (
        <>
          <div className="mb-6">
            <label htmlFor="environment" className="label">
              Select Environment
            </label>
            <select
              id="environment"
              value={selectedEnvironmentId || ""}
              onChange={(e) => setSelectedEnvironmentId(e.target.value || null)}
              className="input max-w-md"
              disabled={environmentsLoading}
            >
              <option value="">Choose an environment...</option>
              {environmentsData?.environments?.map((env) => (
                <option key={env.id} value={env.id}>
                  {env.name}
                  {env.is_production ? " (Production)" : ""}
                  {env.group_name ? ` - ${env.group_name}` : ""}
                </option>
              ))}
            </select>
          </div>

          {selectedEnvironmentId && selectedEnvironment ? (
            <div className="card">
              <div className="mb-4">
                <h2 className="text-lg font-medium text-gray-900">
                  {selectedEnvironment.name}
                </h2>
                {selectedEnvironment.description && (
                  <p className="text-sm text-gray-500 mt-1">
                    {selectedEnvironment.description}
                  </p>
                )}
                <div className="mt-2 flex gap-2">
                  {selectedEnvironment.is_production && (
                    <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">
                      Production
                    </span>
                  )}
                  {selectedEnvironment.approval_required && (
                    <span className="inline-flex items-center rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-medium text-yellow-800">
                      Approval Required
                    </span>
                  )}
                  {selectedEnvironment.group_name && (
                    <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">
                      Group: {selectedEnvironment.group_name}
                    </span>
                  )}
                </div>
                <p className="text-sm text-amber-600 mt-2">
                  Environment-level settings override group defaults.
                </p>
              </div>

              <EnvironmentApproversDeployers
                environmentId={selectedEnvironmentId}
              />
            </div>
          ) : (
            <div className="card text-center py-12">
              <p className="text-gray-500">
                Select an environment above to manage its approvers and
                notifiers. These override any group-level defaults.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Component for managing group-level approvers and notifiers
function GroupApproversNotifiers({ groupId }: { groupId: string }) {
  const [showAddApprover, setShowAddApprover] = useState(false);
  const [showAddNotifier, setShowAddNotifier] = useState(false);
  const [newApprover, setNewApprover] = useState({
    role_id: "",
    user_id: "",
    is_required: true,
  });
  const [newNotifier, setNewNotifier] = useState({
    user_id: "",
    notify_on_approval: true,
  });

  const queryClient = useQueryClient();

  // Fetch approvers
  const { data: approversData, isLoading: approversLoading } = useQuery({
    queryKey: ["group-approvers", groupId],
    queryFn: () => groupApproversApi.list(groupId),
  });

  // Fetch notifiers
  const { data: notifiersData, isLoading: notifiersLoading } = useQuery({
    queryKey: ["group-notifiers", groupId],
    queryFn: () => groupNotifiersApi.list(groupId),
  });

  // Fetch roles for dropdown
  const { data: rolesData } = useQuery({
    queryKey: ["approval-roles"],
    queryFn: () => approvalRolesApi.list(),
  });

  // Fetch all users for notifier selection
  const { data: usersData } = useQuery({
    queryKey: ["users"],
    queryFn: () => usersApi.list(),
  });

  // Fetch eligible approvers for the selected role
  const { data: eligibleApproversData } = useQuery({
    queryKey: ["eligible-approvers", newApprover.role_id],
    queryFn: () => approvalRolesApi.getEligibleApprovers(newApprover.role_id),
    enabled: !!newApprover.role_id,
  });

  const eligibleApprovers = eligibleApproversData?.approvers || [];

  // All active users for notifier selection
  const activeUsers = useMemo(() => {
    if (!usersData?.users) return [];
    return usersData.users.filter((user: User) => user.is_active);
  }, [usersData?.users]);

  // Approver mutations
  const createApproverMutation = useMutation({
    mutationKey: ["createGroupApprover"],
    mutationFn: (data: Parameters<typeof groupApproversApi.create>[1]) =>
      groupApproversApi.create(groupId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["group-approvers", groupId],
      });
      setShowAddApprover(false);
      setNewApprover({
        role_id: "",
        user_id: "",
        is_required: true,
      });
    },
  });

  const deleteApproverMutation = useMutation({
    mutationKey: ["deleteGroupApprover"],
    mutationFn: (approverId: string) =>
      groupApproversApi.delete(groupId, approverId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["group-approvers", groupId],
      });
    },
  });

  // Notifier mutations
  const createNotifierMutation = useMutation({
    mutationKey: ["createGroupNotifier"],
    mutationFn: (data: Parameters<typeof groupNotifiersApi.create>[1]) =>
      groupNotifiersApi.create(groupId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["group-notifiers", groupId],
      });
      setShowAddNotifier(false);
      setNewNotifier({
        user_id: "",
        notify_on_approval: true,
      });
    },
  });

  const deleteNotifierMutation = useMutation({
    mutationKey: ["deleteGroupNotifier"],
    mutationFn: (notifierId: string) =>
      groupNotifiersApi.delete(groupId, notifierId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["group-notifiers", groupId],
      });
    },
  });

  const handleAddApprover = (e: React.FormEvent) => {
    e.preventDefault();
    const selectedApprover = eligibleApprovers.find(
      (a: EligibleApprover) => a.id === newApprover.user_id,
    );
    if (newApprover.role_id && selectedApprover) {
      createApproverMutation.mutate({
        role_id: newApprover.role_id,
        user_email: selectedApprover.email,
        user_name: selectedApprover.name || undefined,
        is_required: newApprover.is_required,
      });
    }
  };

  const handleAddNotifier = (e: React.FormEvent) => {
    e.preventDefault();
    const selectedUser = activeUsers.find(
      (u: User) => u.id === newNotifier.user_id,
    );
    if (selectedUser) {
      createNotifierMutation.mutate({
        user_email: selectedUser.email,
        user_name: selectedUser.name || undefined,
        notify_on_approval: newNotifier.notify_on_approval,
      });
    }
  };

  const activeRoles = rolesData?.roles?.filter((r) => r.is_active) || [];

  return (
    <div className="space-y-8">
      {/* Approvers Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <UserCheck className="h-5 w-5 text-blue-600" />
            <h3 className="text-base font-medium text-gray-900">
              Default Approvers
            </h3>
          </div>
          <button
            onClick={() => setShowAddApprover(!showAddApprover)}
            className="btn btn-secondary text-sm"
          >
            {showAddApprover ? "Cancel" : "+ Add Approver"}
          </button>
        </div>

        {showAddApprover && (
          <form
            onSubmit={handleAddApprover}
            className="bg-gray-50 p-4 rounded-lg mb-4"
          >
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Role</label>
                <select
                  value={newApprover.role_id}
                  onChange={(e) =>
                    setNewApprover({
                      ...newApprover,
                      role_id: e.target.value,
                      user_id: "",
                    })
                  }
                  className="input"
                  required
                >
                  <option value="">Select a role...</option>
                  {activeRoles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">User</label>
                <select
                  value={newApprover.user_id}
                  onChange={(e) =>
                    setNewApprover({
                      ...newApprover,
                      user_id: e.target.value,
                    })
                  }
                  className="input"
                  required
                  disabled={!newApprover.role_id}
                >
                  <option value="">
                    {!newApprover.role_id
                      ? "Select a role first..."
                      : eligibleApprovers.length === 0
                        ? "No eligible approvers for this role"
                        : "Select a user..."}
                  </option>
                  {eligibleApprovers.map((approver: EligibleApprover) => (
                    <option key={approver.id} value={approver.id}>
                      {approver.name} ({approver.email})
                    </option>
                  ))}
                </select>
                {newApprover.role_id && eligibleApprovers.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">
                    No users with this role have the approver flag enabled.
                    Check that the approval role is linked to user roles in
                    Settings.
                  </p>
                )}
              </div>
              <div className="flex items-end pb-2">
                <label className="flex items-center text-sm">
                  <input
                    type="checkbox"
                    checked={newApprover.is_required}
                    onChange={(e) =>
                      setNewApprover({
                        ...newApprover,
                        is_required: e.target.checked,
                      })
                    }
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-2"
                  />
                  Required approval
                </label>
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={
                  createApproverMutation.isPending ||
                  !newApprover.user_id ||
                  !newApprover.role_id
                }
              >
                {createApproverMutation.isPending
                  ? "Adding..."
                  : "Add Approver"}
              </button>
            </div>
          </form>
        )}

        {approversLoading ? (
          <p className="text-sm text-gray-500">Loading approvers...</p>
        ) : !approversData?.approvers?.length ? (
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-500">
              No default approvers configured for this group. Add approvers here
              to apply them to all environments in this group.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {approversData.approvers.map((approver: GroupApprover) => (
              <div
                key={approver.id}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {approver.user_name || approver.user_email}
                    </div>
                    <div className="text-xs text-gray-500">
                      {approver.role_name || "Unknown Role"} •{" "}
                      {approver.user_email}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`px-2 py-0.5 text-xs font-medium rounded ${
                      approver.is_required
                        ? "bg-red-100 text-red-700"
                        : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {approver.is_required ? "Required" : "Optional"}
                  </span>
                  <button
                    onClick={() => deleteApproverMutation.mutate(approver.id)}
                    className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                    disabled={deleteApproverMutation.isPending}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Notifiers Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-green-600" />
            <h3 className="text-base font-medium text-gray-900">
              Deployment Notifiers
            </h3>
          </div>
          <button
            onClick={() => setShowAddNotifier(!showAddNotifier)}
            className="btn btn-secondary text-sm"
          >
            {showAddNotifier ? "Cancel" : "+ Add Notifier"}
          </button>
        </div>

        {showAddNotifier && (
          <form
            onSubmit={handleAddNotifier}
            className="bg-gray-50 p-4 rounded-lg mb-4"
          >
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">User</label>
                <select
                  value={newNotifier.user_id}
                  onChange={(e) =>
                    setNewNotifier({
                      ...newNotifier,
                      user_id: e.target.value,
                    })
                  }
                  className="input"
                  required
                >
                  <option value="">Select a user...</option>
                  {activeUsers.map((user: User) => (
                    <option key={user.id} value={user.id}>
                      {user.name} ({user.email})
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-end pb-2">
                <label className="flex items-center text-sm">
                  <input
                    type="checkbox"
                    checked={newNotifier.notify_on_approval}
                    onChange={(e) =>
                      setNewNotifier({
                        ...newNotifier,
                        notify_on_approval: e.target.checked,
                      })
                    }
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-2"
                  />
                  Notify when all approvals complete
                </label>
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={
                  createNotifierMutation.isPending || !newNotifier.user_id
                }
              >
                {createNotifierMutation.isPending
                  ? "Adding..."
                  : "Add Notifier"}
              </button>
            </div>
          </form>
        )}

        {notifiersLoading ? (
          <p className="text-sm text-gray-500">Loading notifiers...</p>
        ) : !notifiersData?.notifiers?.length ? (
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-500">
              No deployment notifiers configured for this group. Add notifiers
              here to apply them to all environments in this group.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {notifiersData.notifiers.map((notifier: GroupNotifier) => (
              <div
                key={notifier.id}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div>
                  <div className="text-sm font-medium text-gray-900">
                    {notifier.user_name || notifier.user_email}
                  </div>
                  <div className="text-xs text-gray-500">
                    {notifier.user_email}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {notifier.notify_on_approval && (
                    <span className="px-2 py-0.5 text-xs font-medium rounded bg-green-100 text-green-700">
                      Will be notified
                    </span>
                  )}
                  <button
                    onClick={() => deleteNotifierMutation.mutate(notifier.id)}
                    className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                    disabled={deleteNotifierMutation.isPending}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Component for managing environment-level approvers and deployers
function EnvironmentApproversDeployers({
  environmentId,
}: {
  environmentId: string;
}) {
  const [showAddApprover, setShowAddApprover] = useState(false);
  const [showAddDeployer, setShowAddDeployer] = useState(false);
  const [newApprover, setNewApprover] = useState({
    role_id: "",
    user_id: "",
    is_required: true,
  });
  const [newDeployer, setNewDeployer] = useState({
    user_id: "",
    notify_on_approval: true,
  });

  const queryClient = useQueryClient();

  // Fetch approvers
  const { data: approversData, isLoading: approversLoading } = useQuery({
    queryKey: ["environment-approvers", environmentId],
    queryFn: () => environmentApproversApi.list(environmentId),
  });

  // Fetch deployers
  const { data: deployersData, isLoading: deployersLoading } = useQuery({
    queryKey: ["environment-deployers", environmentId],
    queryFn: () => environmentDeployersApi.list(environmentId),
  });

  // Fetch roles for dropdown
  const { data: rolesData } = useQuery({
    queryKey: ["approval-roles"],
    queryFn: () => approvalRolesApi.list(),
  });

  // Fetch all users for notifier selection
  const { data: usersData } = useQuery({
    queryKey: ["users"],
    queryFn: () => usersApi.list(),
  });

  // Fetch eligible approvers for the selected role (uses approval role -> user role mapping)
  const { data: eligibleApproversData } = useQuery({
    queryKey: ["eligible-approvers", newApprover.role_id],
    queryFn: () => approvalRolesApi.getEligibleApprovers(newApprover.role_id),
    enabled: !!newApprover.role_id,
  });

  const eligibleApprovers = eligibleApproversData?.approvers || [];

  // All active users for notifier selection
  const activeUsers = useMemo(() => {
    if (!usersData?.users) return [];
    return usersData.users.filter((user: User) => user.is_active);
  }, [usersData?.users]);

  // Approver mutations
  const createApproverMutation = useMutation({
    mutationKey: ["createEnvironmentApprover"],
    mutationFn: (data: Parameters<typeof environmentApproversApi.create>[1]) =>
      environmentApproversApi.create(environmentId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["environment-approvers", environmentId],
      });
      setShowAddApprover(false);
      setNewApprover({
        role_id: "",
        user_id: "",
        is_required: true,
      });
    },
  });

  const deleteApproverMutation = useMutation({
    mutationKey: ["deleteEnvironmentApprover"],
    mutationFn: (approverId: string) =>
      environmentApproversApi.delete(environmentId, approverId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["environment-approvers", environmentId],
      });
    },
  });

  // Deployer mutations
  const createDeployerMutation = useMutation({
    mutationKey: ["createEnvironmentDeployer"],
    mutationFn: (data: Parameters<typeof environmentDeployersApi.create>[1]) =>
      environmentDeployersApi.create(environmentId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["environment-deployers", environmentId],
      });
      setShowAddDeployer(false);
      setNewDeployer({
        user_id: "",
        notify_on_approval: true,
      });
    },
  });

  const deleteDeployerMutation = useMutation({
    mutationKey: ["deleteEnvironmentDeployer"],
    mutationFn: (deployerId: string) =>
      environmentDeployersApi.delete(environmentId, deployerId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["environment-deployers", environmentId],
      });
    },
  });

  const handleAddApprover = (e: React.FormEvent) => {
    e.preventDefault();
    const selectedApprover = eligibleApprovers.find(
      (a: EligibleApprover) => a.id === newApprover.user_id,
    );
    if (newApprover.role_id && selectedApprover) {
      createApproverMutation.mutate({
        role_id: newApprover.role_id,
        user_email: selectedApprover.email,
        user_name: selectedApprover.name || undefined,
        is_required: newApprover.is_required,
      });
    }
  };

  const handleAddDeployer = (e: React.FormEvent) => {
    e.preventDefault();
    const selectedUser = activeUsers.find(
      (u: User) => u.id === newDeployer.user_id,
    );
    if (selectedUser) {
      createDeployerMutation.mutate({
        user_email: selectedUser.email,
        user_name: selectedUser.name || undefined,
        notify_on_approval: newDeployer.notify_on_approval,
      });
    }
  };

  const activeRoles = rolesData?.roles?.filter((r) => r.is_active) || [];

  return (
    <div className="space-y-8">
      {/* Approvers Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <UserCheck className="h-5 w-5 text-blue-600" />
            <h3 className="text-base font-medium text-gray-900">
              Default Approvers
            </h3>
          </div>
          <button
            onClick={() => setShowAddApprover(!showAddApprover)}
            className="btn btn-secondary text-sm"
          >
            {showAddApprover ? "Cancel" : "+ Add Approver"}
          </button>
        </div>

        {showAddApprover && (
          <form
            onSubmit={handleAddApprover}
            className="bg-gray-50 p-4 rounded-lg mb-4"
          >
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Role</label>
                <select
                  value={newApprover.role_id}
                  onChange={(e) =>
                    setNewApprover({
                      ...newApprover,
                      role_id: e.target.value,
                      user_id: "", // Reset user when role changes
                    })
                  }
                  className="input"
                  required
                >
                  <option value="">Select a role...</option>
                  {activeRoles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">User</label>
                <select
                  value={newApprover.user_id}
                  onChange={(e) =>
                    setNewApprover({
                      ...newApprover,
                      user_id: e.target.value,
                    })
                  }
                  className="input"
                  required
                  disabled={!newApprover.role_id}
                >
                  <option value="">
                    {!newApprover.role_id
                      ? "Select a role first..."
                      : eligibleApprovers.length === 0
                        ? "No eligible approvers for this role"
                        : "Select a user..."}
                  </option>
                  {eligibleApprovers.map((approver: EligibleApprover) => (
                    <option key={approver.id} value={approver.id}>
                      {approver.name} ({approver.email})
                    </option>
                  ))}
                </select>
                {newApprover.role_id && eligibleApprovers.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">
                    No users with this role have the approver flag enabled.
                    Check that the approval role is linked to user roles in
                    Settings.
                  </p>
                )}
              </div>
              <div className="flex items-end pb-2">
                <label className="flex items-center text-sm">
                  <input
                    type="checkbox"
                    checked={newApprover.is_required}
                    onChange={(e) =>
                      setNewApprover({
                        ...newApprover,
                        is_required: e.target.checked,
                      })
                    }
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-2"
                  />
                  Required approval
                </label>
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={
                  createApproverMutation.isPending ||
                  !newApprover.user_id ||
                  !newApprover.role_id
                }
              >
                {createApproverMutation.isPending
                  ? "Adding..."
                  : "Add Approver"}
              </button>
            </div>
          </form>
        )}

        {approversLoading ? (
          <p className="text-sm text-gray-500">Loading approvers...</p>
        ) : !approversData?.approvers?.length ? (
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-500">
              No environment-specific approvers configured. Group defaults will
              be used if available.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {approversData.approvers.map((approver: EnvironmentApprover) => (
              <div
                key={approver.id}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {approver.user_name || approver.user_email}
                    </div>
                    <div className="text-xs text-gray-500">
                      {approver.role_name || "Unknown Role"} •{" "}
                      {approver.user_email}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`px-2 py-0.5 text-xs font-medium rounded ${
                      approver.is_required
                        ? "bg-red-100 text-red-700"
                        : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {approver.is_required ? "Required" : "Optional"}
                  </span>
                  <button
                    onClick={() => deleteApproverMutation.mutate(approver.id)}
                    className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                    disabled={deleteApproverMutation.isPending}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Deployers Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-green-600" />
            <h3 className="text-base font-medium text-gray-900">
              Deployment Notifiers
            </h3>
          </div>
          <button
            onClick={() => setShowAddDeployer(!showAddDeployer)}
            className="btn btn-secondary text-sm"
          >
            {showAddDeployer ? "Cancel" : "+ Add Notifier"}
          </button>
        </div>

        {showAddDeployer && (
          <form
            onSubmit={handleAddDeployer}
            className="bg-gray-50 p-4 rounded-lg mb-4"
          >
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">User</label>
                <select
                  value={newDeployer.user_id}
                  onChange={(e) =>
                    setNewDeployer({
                      ...newDeployer,
                      user_id: e.target.value,
                    })
                  }
                  className="input"
                  required
                >
                  <option value="">Select a user...</option>
                  {activeUsers.map((user: User) => (
                    <option key={user.id} value={user.id}>
                      {user.name} ({user.email})
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-end pb-2">
                <label className="flex items-center text-sm">
                  <input
                    type="checkbox"
                    checked={newDeployer.notify_on_approval}
                    onChange={(e) =>
                      setNewDeployer({
                        ...newDeployer,
                        notify_on_approval: e.target.checked,
                      })
                    }
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-2"
                  />
                  Notify when all approvals complete
                </label>
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={
                  createDeployerMutation.isPending || !newDeployer.user_id
                }
              >
                {createDeployerMutation.isPending
                  ? "Adding..."
                  : "Add Notifier"}
              </button>
            </div>
          </form>
        )}

        {deployersLoading ? (
          <p className="text-sm text-gray-500">Loading deployers...</p>
        ) : !deployersData?.deployers?.length ? (
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-500">
              No environment-specific notifiers configured. Group defaults will
              be used if available.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {deployersData.deployers.map((deployer: EnvironmentDeployer) => (
              <div
                key={deployer.id}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div>
                  <div className="text-sm font-medium text-gray-900">
                    {deployer.user_name || deployer.user_email}
                  </div>
                  <div className="text-xs text-gray-500">
                    {deployer.user_email}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {deployer.notify_on_approval && (
                    <span className="px-2 py-0.5 text-xs font-medium rounded bg-green-100 text-green-700">
                      Will be notified
                    </span>
                  )}
                  <button
                    onClick={() => deleteDeployerMutation.mutate(deployer.id)}
                    className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                    disabled={deleteDeployerMutation.isPending}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
